import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Lock, Loader2, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LazyYouTubeEmbed from "@/components/LazyYouTubeEmbed";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const accent = { fontFamily: "'Playfair Display', serif", fontStyle: "italic" } as const;
const mono = { fontFamily: "'JetBrains Mono', monospace" } as const;

interface VideoItem {
  title?: string;
  youtube_url?: string;
  description?: string;
}
interface PackageItem {
  name?: string;
  price_cents?: number;
  description?: string;
  deliverables?: string[];
}
interface Offer {
  id: string;
  slug: string;
  client_name: string;
  client_company: string | null;
  title: string;
  intro: string | null;
  videos: VideoItem[];
  packages: PackageItem[];
  cta_label: string | null;
  cta_url: string | null;
}

const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

const formatPrice = (cents?: number) => {
  if (typeof cents !== "number") return "";
  return new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

const OfferView = () => {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(false);

  const attempt = async (accessCode: string) => {
    if (!slug || !accessCode.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("offer-access", {
        body: { slug, code: accessCode.trim() },
      });
      if (error) throw new Error(error.message);
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      setOffer((data as { offer: Offer }).offer);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("code")) {
      attempt(params.get("code")!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    attempt(code);
  };

  return (
    <>
      <SEOHead
        title="Proposition"
        description="Proposition personnalisée — Agence 8020"
        canonical={`/offre/${slug}`}
        noIndex
      />

      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <header className="border-b border-border">
          <div className="max-w-[1280px] mx-auto px-6 md:px-8 h-16 flex items-center">
            <Logo variant="full" size="md" />
          </div>
        </header>

        {!offer ? (
          <main className="flex-1 flex items-center justify-center px-6 py-24">
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleSubmit}
              className="w-full max-w-md border border-border rounded-xl p-8 md:p-10 bg-card"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3" style={mono}>
                Accès privé
              </p>
              <h1 className="text-3xl md:text-4xl leading-tight mb-3">
                Votre <span className="text-primary" style={accent}>proposition</span> vous attend
              </h1>
              <p className="text-sm text-muted-foreground mb-6">
                Entrez le code qu'on vous a envoyé pour accéder à votre présentation personnalisée.
              </p>
              <div className="space-y-2">
                <Label htmlFor="code">Code d'accès</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="ex: 8020-XXXX"
                  autoFocus
                  autoComplete="off"
                  maxLength={100}
                />
              </div>
              <Button type="submit" variant="hero" size="lg" className="w-full mt-6" disabled={loading || !code.trim()}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Accéder <ArrowRight className="w-4 h-4" /></>}
              </Button>
            </motion.form>
          </main>
        ) : (
          <main className="flex-1">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
              </div>
              <div className="relative max-w-4xl mx-auto px-6 md:px-8 py-24 md:py-32">
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs uppercase tracking-[0.25em] text-primary mb-6"
                  style={mono}
                >
                  Proposition · Agence 8020
                </motion.p>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-4xl md:text-6xl leading-[1.05] tracking-tight mb-6"
                >
                  Bonjour <span className="text-primary" style={accent}>{offer.client_name}</span>
                  {offer.client_company ? <span className="text-muted-foreground text-2xl md:text-3xl block mt-2">{offer.client_company}</span> : null}
                </motion.h1>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl md:text-3xl text-foreground/90 leading-snug mb-6"
                >
                  {offer.title}
                </motion.h2>
                {offer.intro && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-lg text-foreground/80 leading-relaxed whitespace-pre-wrap"
                  >
                    {offer.intro}
                  </motion.div>
                )}
              </div>
            </section>

            {/* Videos */}
            {offer.videos && offer.videos.length > 0 && (
              <section className="max-w-6xl mx-auto px-6 md:px-8 py-24 md:py-32">
                <p className="text-xs uppercase tracking-[0.25em] text-primary mb-4" style={mono}>
                  Exemples
                </p>
                <h2 className="text-3xl md:text-5xl leading-tight mb-12">
                  Ce qu'on a <span className="text-primary" style={accent}>déjà tourné</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {offer.videos.map((v, i) => {
                    const id = extractYouTubeId(v.youtube_url || "");
                    if (!id) return null;
                    return (
                      <div key={i} className="space-y-3">
                        <div className="rounded-xl overflow-hidden border border-border">
                          <LazyYouTubeEmbed videoId={id} title={v.title || `Vidéo ${i + 1}`} autoplay={false} />
                        </div>
                        {v.title && <h3 className="text-lg font-medium">{v.title}</h3>}
                        {v.description && (
                          <p className="text-sm text-muted-foreground leading-relaxed">{v.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Packages */}
            {offer.packages && offer.packages.length > 0 && (
              <section className="border-t border-border">
                <div className="max-w-6xl mx-auto px-6 md:px-8 py-24 md:py-32">
                  <p className="text-xs uppercase tracking-[0.25em] text-primary mb-4" style={mono}>
                    Investissement
                  </p>
                  <h2 className="text-3xl md:text-5xl leading-tight mb-12">
                    <span className="text-primary" style={accent}>Prix</span> et livrables
                  </h2>
                  <div className={`grid grid-cols-1 ${offer.packages.length > 1 ? "md:grid-cols-2 lg:grid-cols-3" : ""} gap-6`}>
                    {offer.packages.map((p, i) => (
                      <div key={i} className="border border-border rounded-xl p-8 bg-card flex flex-col">
                        <h3 className="text-xl font-semibold mb-2">{p.name}</h3>
                        {typeof p.price_cents === "number" && (
                          <p className="text-3xl text-primary mb-4" style={mono}>
                            {formatPrice(p.price_cents)}
                          </p>
                        )}
                        {p.description && (
                          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{p.description}</p>
                        )}
                        {p.deliverables && p.deliverables.length > 0 && (
                          <ul className="space-y-2 mt-auto">
                            {p.deliverables.map((d, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                <span>{d}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Final CTA */}
            {offer.cta_url && offer.cta_label && (
              <section className="border-t border-border">
                <div className="max-w-3xl mx-auto px-6 md:px-8 py-24 md:py-32 text-center">
                  <h2 className="text-3xl md:text-5xl leading-tight mb-8">
                    On <span className="text-primary" style={accent}>embarque</span>?
                  </h2>
                  <Button asChild variant="hero" size="xl">
                    <a href={offer.cta_url} target="_blank" rel="noopener noreferrer">
                      {offer.cta_label}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </section>
            )}
          </main>
        )}

        <footer className="border-t border-border">
          <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-8 text-center text-xs text-muted-foreground" style={mono}>
            © {new Date().getFullYear()} Agence 8020 — Document confidentiel
          </div>
        </footer>
      </div>
    </>
  );
};

export default OfferView;
